import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ProspectStatusChip from './ProspectStatusChip';
import { externalHref, formatSubmittedDate, leadAgentName } from './prospectFields';

const DetailItem = ({ label, children }) => (
  <Box>
    <Typography variant='body2' fontWeight={700} color='#0f172a' mb={0.5}>
      {label}
    </Typography>
    <Typography variant='body2' color='#334155' sx={{ wordBreak: 'break-word' }}>
      {children}
    </Typography>
  </Box>
);

const ExternalValue = ({ value }) => {
  const href = externalHref(value);
  if (!href) return '—';
  return (
    <Link
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      underline='hover'
      sx={{ display: 'inline-flex', gap: 0.5 }}
    >
      {value}
      <OpenInNewIcon sx={{ fontSize: 14, mt: '2px' }} />
    </Link>
  );
};

const NoteBlock = ({ label, value }) => (
  <Box sx={{ bgcolor: '#f4f7fb', borderRadius: 2, p: 2 }}>
    <Typography variant='body2' fontWeight={700} color='#0f172a' mb={0.75}>
      {label}
    </Typography>
    <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'pre-wrap' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const ClientProspectDetailsModal = ({ open, prospect, onClose }) => {
  if (!prospect) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Prospect details</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' mb={2.5}>
          Review the information submitted by the Lead Agent.
        </Typography>
        <Box display='grid' gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2.5} mb={2.5}>
          <DetailItem label='Company'>{prospect.company || '—'}</DetailItem>
          <DetailItem label='Contact name'>{prospect.contactName || '—'}</DetailItem>
          <DetailItem label='Title'>{prospect.title || '—'}</DetailItem>
          <DetailItem label='Submitted by'>{leadAgentName(prospect.leadAgent) || '—'}</DetailItem>
          <DetailItem label='Program'>{prospect.program?.productName || '—'}</DetailItem>
          <Box />
          <DetailItem label='Website'>
            <ExternalValue value={prospect.website} />
          </DetailItem>
          <DetailItem label='LinkedIn'>
            <ExternalValue value={prospect.linkedin} />
          </DetailItem>
        </Box>
        <Box display='flex' flexDirection='column' gap={1.5}>
          <NoteBlock label='How do you know this person?' value={prospect.howDoYouKnowThisPerson} />
          <NoteBlock label='Why could this be relevant?' value={prospect.whyRelevant} />
          <NoteBlock label='Anything else I should know?' value={prospect.anythingElse} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between', alignItems: 'center' }}>
        <Box display='flex' alignItems='center' gap={3}>
          <Box>
            <Typography variant='caption' color='text.secondary' display='block'>
              Status
            </Typography>
            <ProspectStatusChip status={prospect.status} />
          </Box>
          <Box>
            <Typography variant='caption' color='text.secondary' display='block'>
              Date submitted
            </Typography>
            <Typography variant='body2'>{formatSubmittedDate(prospect.submittedAt)}</Typography>
          </Box>
        </Box>
        <Button variant='outlined' onClick={onClose} sx={{ textTransform: 'none', borderRadius: 2 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientProspectDetailsModal;
